import { useMemo } from 'react'
import { SectionItem } from '@features/shift-post-v2/components'
import { Box, Flex, Text, InputV2, SwitchV2 } from '@pinchenterprisesnpm/friday-ui'
import { InfoBanner } from '@src/features/shift-post-v2/components/info-banner/info-banner'
import { connect } from 'formik'
import { SHIFT_TYPE } from '@src/models/Shift'
import { MultiSingleDayPicker } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/multi-single-day-picker/multi-single-day-picker'
import { IndividualDaysList } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/individual-days-list/indivitual-days-list'
import { shouldShowOTPolicyWarningV2 } from '@src/helpers/shiftHelpers'
import { getBreakValue } from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils'
import { faAlarmClock } from '@fortawesome/pro-solid-svg-icons'
import { faDollarSign } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'
import { selectBusinessEntity } from '@src/data/BusinessEntity/selectors'
import { OTPolicyWarning } from 'ui/components/Admin/OTPolicyWarning'
import { ShiftPostPage } from '@src/features/shift-post-v2/pages'
import { SingleDayPicker } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/single-day-picker/single-day-picker'
import { ShiftPostStep } from '@src/features/shift-post-v2/steps/steps.types'
import { useModal } from '@src/hooks/useModal'
import { EditScheduleModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/modals/edit-schedule-modal/edit-schedule-modal'
import { ScheduleSectionContainer } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.style'
import { EditShiftModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/modals/edit-shift-modal/edit-shift-modal'
import { RecurringDayPicker } from '@features/shift-post-v2/components'
import { PayRateFeedback } from '@src/features/shift-post-v2/modals/new-edit-modal/new-edit-modal/components'
import { CustomTimePicker } from './components/custom-time-picker'
import CustomDropdownV5 from './components/custom-dropdown-v5/custom-dropdown-v5'
import { handlePayRateChange, handleHourChange, handleBreakChange } from './form-handlers'
import { IScheduleSectionProps } from './schedule-section.types'

export const ScheduleSection = connect<IScheduleSectionProps>(
  ({ formik, sourcePage, sourceStep }) => {
    const [isEditModalOpen, toggleEditModal, closeEditModal] = useModal()
    const [isEditShiftModalOpen, toggleEditShiftModal, closeEditShiftModal] = useModal()

    const { values, setFieldValue, handleBlur, errors } = formik
    const { location } = values
    const businessEntity = useSelector(selectBusinessEntity)

    const isConfirmStep = sourceStep === ShiftPostStep.CONFIRM

    const payRateLimit = location?.active_overtime_policy?.pay_rate_limit

    const showOTPolicyWarning = payRateLimit
      ? shouldShowOTPolicyWarningV2(values, payRateLimit, sourcePage === ShiftPostPage.EDIT_SHIFT)
      : false

    /**
     * Field editing is disabled if template is not selected.
     */
    const editingDisabled = sourcePage === ShiftPostPage.POST_SHIFT && !values.template_id

    const isEditShiftPage = sourcePage === ShiftPostPage.EDIT_SHIFT

    const onlyOneDay = isEditShiftPage
      ? !!values.single_day_date
      : values.single_day_shifts?.length === 1
    const isSingleDay = values.type === SHIFT_TYPE.SINGLEDAY

    const shouldShowEditShiftModal = onlyOneDay && isSingleDay

    // Check if dates are selected for Confirm step validation
    const hasDateSelected = useMemo(() => {
      if (!isConfirmStep) return true

      if (isSingleDay) {
        if (isEditShiftPage) {
          return !!values.single_day_date
        } else {
          return values.single_day_shifts && values.single_day_shifts.length > 0
        }
      } else {
        // MultiDay shift
        return !!(values.multiday_day_start && values.multiday_day_end)
      }
    }, [
      isConfirmStep,
      isSingleDay,
      isEditShiftPage,
      values.single_day_date,
      values.single_day_shifts,
      values.multiday_day_start,
      values.multiday_day_end,
    ])

    if (isConfirmStep) {
      return (
        <SectionItem
          title='Schedule'
          hasEdit
          isEditDisabled={editingDisabled}
          editDisabledTooltip='Please select a template first to edit the schedule.'
          onEdit={shouldShowEditShiftModal ? toggleEditShiftModal : toggleEditModal}
        >
          <ScheduleSectionContainer>
            {!hasDateSelected && (
              <>
                <Text fontSize='medium' mt='xs'>
                  No date selected.
                </Text>
                <Text color='danger' my='0' mb='md' fontSize='small'>
                  Please select a date for your shift.
                </Text>
              </>
            )}
            <IndividualDaysList
              sourcePage={sourcePage}
              sourceStep={sourceStep}
              isEditable={false}
              businessEntity={businessEntity}
            />
          </ScheduleSectionContainer>

          <EditScheduleModal
            isOpen={isEditModalOpen}
            closeModal={closeEditModal}
            sourcePage={sourcePage}
          />

          {shouldShowEditShiftModal && (
            <EditShiftModal
              customIsOpen={isEditShiftModalOpen}
              customCloseModal={closeEditShiftModal}
              hasCustomToggleButton
              day={isEditShiftPage ? undefined : values.single_day_shifts?.[0]}
              singleDayDate={isEditShiftPage ? values.single_day_date : undefined}
              directlyEditPayRate
            />
          )}
        </SectionItem>
      )
    }

    return (
      <SectionItem title='Schedule'>
        {!isSingleDay && (
          <Box mt='sm'>
            <InfoBanner description='The date filled here will be reflect on each day individually.' />
          </Box>
        )}

        <Flex alignItems='center'>
          <Text mr='sm' color='gray'>
            Recurring Shift
          </Text>

          <SwitchV2
            value={values.is_recurring_shift}
            onChange={(value: boolean) => {
              setFieldValue('is_recurring_shift', value)
              setFieldValue('type', value ? SHIFT_TYPE.MULTIDAY : SHIFT_TYPE.SINGLEDAY)
            }}
          />
        </Flex>

        {/* Date picker */}
        <Flex mt='md'>
          <Box mr='md'>
            {values.type === SHIFT_TYPE.SINGLEDAY &&
              [ShiftPostPage.POST_SHIFT, ShiftPostPage.COPY_SHIFT].includes(sourcePage) && (
                <MultiSingleDayPicker isDisabled={editingDisabled} customWidth='420px' />
              )}

            {values.type === SHIFT_TYPE.SINGLEDAY && sourcePage === ShiftPostPage.EDIT_SHIFT && (
              <SingleDayPicker isDisabled={editingDisabled} customWidth='420px' />
            )}

            {values.type === SHIFT_TYPE.MULTIDAY && (
              <RecurringDayPicker formik={formik as any} isDisabled={editingDisabled} />
            )}
          </Box>
        </Flex>

        {/* Time picker */}
        <Flex mt='md'>
          <Box mr='md'>
            <CustomTimePicker
              value={values.hour_start}
              onChange={(value) => handleHourChange(value, true, formik)}
              label='Start Time'
              hasError={!!errors.hour_start}
              width='200px'
              isDisabled={editingDisabled}
            />
          </Box>

          <Box>
            <CustomTimePicker
              value={values.hour_end}
              label='End Time'
              width='200px'
              hasError={!!errors.hour_end}
              onChange={(value) => handleHourChange(value, false, formik)}
              isDisabled={editingDisabled}
            />
          </Box>
        </Flex>

        {/* Pay rate and unpaid break */}
        <Flex mt='md'>
          <Flex mr='md' alignItems='center'>
            <InputV2
              leftElement={
                <FontAwesomeIcon
                  icon={faDollarSign}
                  size='lg'
                  color='#C5C5C7'
                  style={{
                    height: 24,
                    width: 24,
                  }}
                />
              }
              labelStyles={{
                color: '#868686',
                fontWeight: 400,
              }}
              leftElementStyles={{
                top: 8,
                left: -1,
              }}
              numberArrowIconColor='gray.400'
              numberArrowsWrapperStyles={{
                right: 3,
              }}
              label='Hourly Pay Rate'
              placeholder=''
              type='number'
              name='pay_value'
              id='pay_value'
              width={'200px'}
              value={values.pay_value}
              isDisabled={editingDisabled}
              onChange={(e) => handlePayRateChange(e, formik)}
              onBlur={handleBlur}
              errorMessage={errors.pay_value}
              onKeyDown={(e) => {
                if (['e', 'E', '-', '+'].includes(e.key)) {
                  e.preventDefault()
                }
              }}
            />
          </Flex>

          <Box height='50px'>
            <CustomDropdownV5
              topLabel='Unpaid break'
              icon={faAlarmClock}
              options={getBreakValue()}
              value={
                getBreakValue().find((opt) => opt.value === (values.break || 0)) ||
                getBreakValue()[0]
              }
              onChange={(selected: any) => handleBreakChange(selected, formik)}
              isDisabled={editingDisabled}
            />
          </Box>
        </Flex>

        {showOTPolicyWarning && (
          <Box>
            <OTPolicyWarning payRate={payRateLimit ?? 0} />
          </Box>
        )}
        <PayRateFeedback
          suggestedPayRate={Number(values.suggested_pay_rate)}
          payValue={Number(values.pay_value)}
        />
        <IndividualDaysList sourcePage={sourcePage} sourceStep={sourceStep} />
      </SectionItem>
    )
  }
)
